import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LeftOutlined, RightOutlined, XOutlined } from '@ant-design/icons';
import TwitterCard from './TwitterCard';
import { twitterTestimonials } from '../data/testimonials';

// Twitter 卡片数据类型
export interface TwitterCardData {
  id: string;
  userName: string;
  userHandle: string;
  userAvatar: string;
  content: string;
  image?: string; // 单张图片
  link: string;
}

const TwitterSection: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // 从数据配置文件导入数据
  const twitterCards: TwitterCardData[] = twitterTestimonials;

  // 检查滚动位置
  const checkScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      if (cardWidth > 0) {
        const maxIndex = twitterCards.length - 1;
        const rawIndex = Math.round(scrollLeft / cardWidth);
        const clampedIndex = Math.min(maxIndex, Math.max(0, rawIndex));
        if (clampedIndex !== currentIndex) {
          setCurrentIndex(clampedIndex);
        }
        if (maxIndex <= 0) {
          setCanScrollLeft(false);
          setCanScrollRight(false);
        }
      } else {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    }
  }, [cardWidth, currentIndex, twitterCards.length]);

  // 滚动函数
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      if (cardWidth > 0) {
        const maxIndex = twitterCards.length - 1;
        const nextIndex = direction === 'left' ? Math.max(0, currentIndex - 1) : Math.min(maxIndex, currentIndex + 1);
        const container = scrollContainerRef.current;
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const targetScrollLeft = Math.min(nextIndex * cardWidth, maxScrollLeft);
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
        setCurrentIndex(nextIndex);
      } else {
        const scrollAmount = scrollContainerRef.current.clientWidth;
        const newScrollLeft =
          direction === 'left'
            ? scrollContainerRef.current.scrollLeft - scrollAmount
            : scrollContainerRef.current.scrollLeft + scrollAmount;

        scrollContainerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth',
        });

        setTimeout(checkScrollPosition, 300);
      }
    }
  };

  useEffect(() => {
    const updateCardMeasurements = () => {
      if (!scrollContainerRef.current) return;
      const container = scrollContainerRef.current;
      const firstCard = container.querySelector<HTMLElement>('[data-twitter-card="true"]');
      if (!firstCard) return;
      const styles = window.getComputedStyle(container);
      const columnGapRaw = parseFloat(styles.columnGap || '0');
      const gapFromColumn = Number.isNaN(columnGapRaw) ? 0 : columnGapRaw;
      const gapFromShorthand = (() => {
        if (!styles.gap) return 0;
        const [firstValue] = styles.gap.split(' ');
        const parsed = parseFloat(firstValue);
        return Number.isNaN(parsed) ? 0 : parsed;
      })();
      const gapValue = gapFromColumn || gapFromShorthand;
      const measuredWidth = firstCard.getBoundingClientRect().width + gapValue;
      if (Math.abs(measuredWidth - cardWidth) > 1) {
        setCardWidth(measuredWidth);
      }
    };

    updateCardMeasurements();
    window.addEventListener('resize', updateCardMeasurements);

    return () => {
      window.removeEventListener('resize', updateCardMeasurements);
    };
  }, [cardWidth, twitterCards.length]);

  useEffect(() => {
    if (cardWidth > 0) {
      const maxIndex = twitterCards.length - 1;
      setCanScrollLeft(currentIndex > 0);
      setCanScrollRight(currentIndex < maxIndex);
    }
  }, [cardWidth, currentIndex, twitterCards.length]);

  useEffect(() => {
    checkScrollPosition();
  }, [cardWidth, checkScrollPosition]);

  return (
    <div className="py-[60px] max-lg:py-10">
      {/* 标题和导航箭头 */}
      <div className="flex justify-between items-center mb-8 px-2 lg:px-0">
        <div className="flex items-center gap-3 max-md:gap-2">
          <XOutlined className="text-2xl " />
          <h2 className="text-[32px] max-lg:text-[28px] max-md:text-2xl font-bold text-[#0c0c0c] m-0">Twitter</h2>
        </div>
        <div className="flex gap-3 max-md:gap-2">
          <button
            className={`size-8 rounded-lg border border-[#e0e0e0] bg-white text-[#333333] cursor-pointer transition-all duration-300 flex items-center justify-center text-base max-md:text-sm ${
              !canScrollLeft
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:border-[var(--app-primary-color,#725CFF)] hover:text-[var(--app-primary-color,#725CFF)] hover:shadow-[0_2px_8px_rgba(114,92,255,0.2)]'
            }`}
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            <LeftOutlined />
          </button>
          <button
            className={`size-8 rounded-lg border border-[#e0e0e0] bg-white text-[#333333] cursor-pointer transition-all duration-300 flex items-center justify-center text-base max-md:text-sm ${
              !canScrollRight
                ? 'opacity-30 cursor-not-allowed'
                : 'hover:border-[var(--app-primary-color,#725CFF)] hover:text-[var(--app-primary-color,#725CFF)] hover:shadow-[0_2px_8px_rgba(114,92,255,0.2)]'
            }`}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            <RightOutlined />
          </button>
        </div>
      </div>

      {/* 卡片容器 */}
      <div className="relative">
        <div
          className="flex gap-6 max-lg:gap-4 max-md:gap-3 overflow-x-auto pb-5 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden px-2 lg:px-0"
          ref={scrollContainerRef}
          onScroll={checkScrollPosition}
        >
          {twitterCards.map((card) => (
            <TwitterCard key={card.id} data={card} />
          ))}
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 hidden w-20 bg-gradient-to-l from-white via-white/60 to-transparent lg:block"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default TwitterSection;
